import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { useAsideContext } from '@contexts/AsideContext';
import { ApiResponse } from '@services/core/AbstractService';

interface CrudService<TFormData, TData> {
  create?: (data: TFormData) => Promise<ApiResponse<TData>>;
  update?: (id: string | number, data: Partial<TFormData>) => Promise<ApiResponse<TData>>;
  remove?: (id: string | number) => Promise<ApiResponse<void>>;
}

interface UseCrudMessages {
  created?: string;
  updated?: string;
  deleted?: string;
}

interface UseCrudOptions<TFormData, TData> {
  /** React Query key this resource's list/table is cached under */
  queryKey: string;
  service: CrudService<TFormData, TData>;
  messages?: UseCrudMessages;
  /** Close the global Aside on successful create/update (default: true) */
  closeAsideOnSuccess?: boolean;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as AxiosError<{ message?: string }>;
  return axiosError.response?.data?.message || fallback;
};

/**
 * Wraps create/update/remove mutations for a resource with toast feedback,
 * query-cache invalidation, and aside-close-on-success — the pattern every
 * CRUD page in the app repeats today.
 */
export function useCrud<TFormData, TData = unknown>({
  queryKey,
  service,
  messages,
  closeAsideOnSuccess = true,
}: UseCrudOptions<TFormData, TData>) {
  const queryClient = useQueryClient();
  const { closeAside } = useAsideContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [queryKey] });

  const handleCreate = async (data: TFormData): Promise<TData | undefined> => {
    if (!service.create) return undefined;
    setIsSubmitting(true);
    try {
      const response = await service.create(data);
      toast.success(messages?.created ?? 'Created successfully');
      invalidate();
      if (closeAsideOnSuccess) closeAside();
      return response.data;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create'));
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (id: string | number, data: Partial<TFormData>): Promise<void> => {
    if (!service.update) return;
    setIsSubmitting(true);
    try {
      await service.update(id, data);
      toast.success(messages?.updated ?? 'Updated successfully');
      invalidate();
      if (closeAsideOnSuccess) closeAside();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update'));
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string | number): Promise<void> => {
    if (!service.remove) return;
    setIsSubmitting(true);
    try {
      await service.remove(id);
      toast.success(messages?.deleted ?? 'Deleted successfully');
      invalidate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete'));
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { handleCreate, handleEdit, handleDelete, isSubmitting };
}
